import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import "../../../../css/AML/Checks/NewCheck.css";
import RadioButton from "../Components/Buttons/RadioButton";
import EnhancedCheck from "./Check/Enhanced";
import StandardCheck from "./Check/Standard";
import SimpleCheck from "./Check/Simple";
import PEPSanctionsCheck from "./Check/PEPSanctions";
import { useState, useEffect } from "react";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { useUserPermissions } from "../../../../context/UserPermissionsContext";
import { useNavigate } from "react-router-dom";
import { useAdminStatus } from "../../../../context/AdminStatusContext";
import Banner from "../Components/Banners/Banner";
import { useAdminAdviserMails } from "../../../../context/AdminAdviserEmailsContext";
import { useBranding } from "../../../../context/BrandingContext";
import { canSubmitSimpleCheck } from "./utils/helperFunctions/canSubmitSimpleCheck";
// import { canAccessAML } from "../Utils/permissions";
import { ReactComponent as Check2Logo } from "../../../../assets/icons/aml/check2_logo.svg";
import { ReactComponent as QuestionMark } from "../../../../assets/icons/aml/question-mark-tooltip.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/aml/information.svg";
import CheckHelpModal from "./Components/CheckHelpModal";
import { useCheck2GuideUrl } from "../Checks/utils/guide/getUrl";
import { isSimpleCheckUser } from "./utils/helperFunctions/isSimpleCheckUser";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import getAllAdvisers from "../../../../context/HelperFunctions/getAllAdvisers"; // adjust path if needed

const recruitmentOption = {
  id: "Recruitment@rosemountfs.co.uk",
  name: "Recruitment (Recruitment@rosemountfs.co.uk)",
  adviserBrandName: "Rosemount Financial Solutions (IFA) Ltd",
};

const NewCheck = () => {
  const [selectedCheck, setSelectedCheck] = useState("Enhanced"); // default
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const navigate = useNavigate();
  const { adminStatus } = useAdminStatus();
  const isAdviserAdmin = userRole === "Administrators";
  const { currentAdviserEmail } = useAdminAdviserMails();

  const [showCheckHelp, setShowCheckHelp] = useState(false);
  const [canSeeAML, setCanSeeAML] = useState(false);
  const check2GuideUrl = useCheck2GuideUrl();

  const [isSimpleUser, setIsSimpleUser] = useState(false);
  const [loadingSimpleUser, setLoadingSimpleUser] = useState(true);

  const isStaff = userRole === "Staff";

  const [staffSelectedAdviserEmail, setStaffSelectedAdviserEmail] =
    useState(recruitmentOption.id);
  const [adviserOptions, setAdviserOptions] = useState([recruitmentOption]);
  const [loadingAdvisers, setLoadingAdvisers] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadAdvisers = async () => {
      if (!isStaff) {
        setStaffSelectedAdviserEmail(recruitmentOption.id);
        setAdviserOptions([recruitmentOption]);
        return;
      }

      try {
        setLoadingAdvisers(true);

        const advisers = await getAllAdvisers();

        if (!mounted) return;

        const options = advisers
          .map((adviser) => {
            const email = adviser.email || adviser.userId;
            const adviserBrandName =
              adviser.tradingName || adviser.brandName || "";

            return {
              id: email,
              name: adviser.email || email,
              adviserBrandName,
              // adviser.tradingName ||
              // adviser.preferredName ||
              // adviser.displayName ||
              // email,
            };
          })
          .filter(
            (option) =>
              option.id &&
              option.id.toLowerCase() !== recruitmentOption.id.toLowerCase()
          )
          .concat(recruitmentOption)
          .sort((a, b) => a.name.localeCompare(b.name));

        setAdviserOptions(options);
      } catch (err) {
        console.error("Failed to load advisers", err);
        if (mounted) setAdviserOptions([recruitmentOption]);
      } finally {
        if (mounted) setLoadingAdvisers(false);
      }
    };

    loadAdvisers();

    return () => {
      mounted = false;
    };
  }, [isStaff]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        if (!userEmail) {
          setIsSimpleUser(false);
          return;
        }

        const result = await isSimpleCheckUser(userEmail);
        setIsSimpleUser(result);
      } catch (err) {
        console.error("Failed to check simple check user", err);
        setIsSimpleUser(false);
      } finally {
        setLoadingSimpleUser(false);
      }
    };

    checkUser();
  }, [userEmail]);

  const { tradingName } = useBranding();

  const canSubmit =
    !loadingSimpleUser &&
    (canSubmitSimpleCheck({
      userRole,
      userPermissions,
      tradingName,
    }) ||
      isSimpleUser);

  // useEffect(() => {
  //   let mounted = true;

  //   async function checkAccess() {
  //     const allowed = await canAccessAML({
  //       userRole,
  //       userPermissions,
  //       adminEmail: userEmail,
  //     });

  //     if (mounted) setCanSeeAML(allowed);
  //   }

  //   checkAccess();

  //   return () => {
  //     mounted = false;
  //   };
  // }, [userRole, userPermissions, userEmail]);

  const renderCheckComponent = () => {
    const sharedProps = {
      selectedForAdviserEmail,
      selectedForAdviserBrandName,
    };

    switch (selectedCheck) {
      case "Enhanced":
        return <EnhancedCheck {...sharedProps} />;

      case "Standard":
        return <StandardCheck {...sharedProps} />;

      case "Simple":
        if (!canSubmit) {
          return null;
        }
        return <SimpleCheck {...sharedProps} />;

      case "PEP & Sanctions":
        return <PEPSanctionsCheck {...sharedProps} />;

      default:
        return null;
    }
  };

  // useEffect(() => {
  //   if (canSeeAML) {
  //     navigate("/MembersHub");
  //   }
  // }, [userEmail, navigate, userRole, userPermissions, adminStatus]);

  const mustPickAdviser = isAdviserAdmin && !currentAdviserEmail;

  const selectedForAdviserEmail = isStaff
    ? staffSelectedAdviserEmail || recruitmentOption.id
    : currentAdviserEmail;

  const selectedStaffAdviser = adviserOptions.find(
    (option) => option.id === selectedForAdviserEmail
  );

  const selectedForAdviserBrandName = isStaff
    ? selectedStaffAdviser?.adviserBrandName || tradingName
    : tradingName;

  return (
    <div className="Main-Page">
      <Helmet>
        <title>
          {selectedCheck === "PEP & Sanctions"
            ? "PEP & Sanctions Check"
            : `${selectedCheck} Check`}
        </title>
        <meta name="description" content="New Check" />
      </Helmet>
      <Sidebar />
      <div className="new-aml-check-container">
        <div className="new-aml-check-logo-header">
          <Check2Logo />
          <div className="new-aml-check-helper-row">
            <div
              className="new-aml-check-logo-header"
              style={{ gap: "10px", cursor: "pointer" }}
              onClick={() => setShowCheckHelp(true)}
            >
              <div className="AML-Text-Body">Help</div>
              <QuestionMark className="InfoIcon" />
            </div>
            <div
              className="new-aml-check-logo-header"
              style={{
                gap: "10px",
                cursor: check2GuideUrl ? "pointer" : "not-allowed",
                opacity: check2GuideUrl ? 1 : 0.5,
              }}
              onClick={() => {
                if (!check2GuideUrl) return;
                window.open(check2GuideUrl, "_blank", "noopener,noreferrer");
              }}
            >
              <div className="AML-Text-Body">Guide</div>
              <InfoIcon className="InfoIcon" />
            </div>
          </div>
        </div>

        <div className="new-aml-check-header">
          {/* <div className="AML-Text-Title">Select a Check</div> */}
          {mustPickAdviser && (
            <Banner
              type="warning"
              message="Please select an adviser in the sidebar before creating a check."
            />
          )}

          {!mustPickAdviser && (
            <div className="new-aml-check-row">
              <RadioButton
                label="Enhanced"
                checked={selectedCheck === "Enhanced"}
                onChange={() => setSelectedCheck("Enhanced")}
              />
              <RadioButton
                label="Standard"
                checked={selectedCheck === "Standard"}
                onChange={() => setSelectedCheck("Standard")}
              />
              {canSubmit && (
                <RadioButton
                  label="Simple"
                  checked={selectedCheck === "Simple"}
                  onChange={() => setSelectedCheck("Simple")}
                />
              )}
              <RadioButton
                label="PEP & Sanctions"
                checked={selectedCheck === "PEP & Sanctions"}
                onChange={() => setSelectedCheck("PEP & Sanctions")}
              />
            </div>
          )}
        </div>
        {isStaff && (
          <CustomSelect
            label="Select an adviser"
            required
            options={adviserOptions}
            value={staffSelectedAdviserEmail || recruitmentOption.id}
            onChange={(e) =>
              setStaffSelectedAdviserEmail(e.target.value || recruitmentOption.id)
            }
            placeholder={
              loadingAdvisers
                ? "Loading advisers..."
                : "Select an adviser"
            }
            searchPlaceholder="Search advisers..."
            disabled={loadingAdvisers}
            clearable
            searchable
          />
        )}
        {selectedCheck !== "Simple" && (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => setShowCheckHelp(true)}
          >
            <Banner
              type="warning"
              message="Unsure which check to select? Click here to help you decide."
            />
          </div>
        )}
        {/* {isAdviserAdmin && currentAdviserEmail && (
          <Banner
            type="warning"
            message={`This check is being requested on behalf of ${currentAdviserEmail}. Please ensure you have their consent before proceeding.`}
          />
        )} */}

        {selectedForAdviserEmail && (
          <Banner
            type="warning"
            message={`This check is being requested on behalf of ${selectedForAdviserEmail}. Please ensure you have their consent before proceeding.`}
          />
        )}

        {!mustPickAdviser && renderCheckComponent()}
      </div>

      <CheckHelpModal
        open={showCheckHelp}
        onClose={() => setShowCheckHelp(false)}
        canShowSimple={canSubmit}
      />
    </div>
  );
};
export default NewCheck;
